import { useQueries } from "@tanstack/react-query";
import {
  FetchApiError,
  fetchFunction,
  useFetchApi,
  UseFetchDataResponse
} from "../ssc-api/useFetchApi";

type GatsbyPageData = {
  componentChunkName: string;
  path: string;
  result: {
    data: unknown;
  };
};

const useFetchModalData = <T>(
  path: string | undefined
): UseFetchDataResponse<T> => {
  const pathOrUndefined = path
    ? `/page-data/modals${path.replace(".html", "")}/page-data.json`
    : undefined;
  const { status, data, isError, isLoading, refetch } =
    useFetchApi<GatsbyPageData>(pathOrUndefined, undefined, false);
  if (status == "success")
    return {
      isError,
      isLoading,
      data: data?.result?.data as T,
      status,
      refetch
    };
  return { status, isError, isLoading, data: undefined, refetch };
};

export default useFetchModalData;

const modalPageDataUrl = (path: string) =>
  `/page-data/modals${path.replace(".html", "")}/page-data.json`;

const isNotFound = (error: unknown) =>
  error instanceof FetchApiError && error.statusCode === 404;

/**
 * Fetches the page-data of several modal pages in parallel. A missing modal
 * (404) is not an error: its entry is simply left out of `data`.
 */
export const useFetchModalDataList = <T>(
  paths: string[] | undefined
): { isLoading: boolean; isError: boolean; data: T[] | undefined } => {
  const urls = (paths ?? []).map(modalPageDataUrl);
  return useQueries({
    queries: urls.map((url) => ({
      queryKey: [url],
      queryFn: fetchFunction<GatsbyPageData>(url),
      retry: false
    })),
    combine: (results) => {
      const isLoading = results.some((r) => r.isPending);
      return {
        isLoading,
        isError: results.some((r) => r.isError && !isNotFound(r.error)),
        data: isLoading
          ? undefined
          : results
              .filter((r) => r.data)
              .map((r) => (r.data as GatsbyPageData).result.data as T)
      };
    }
  });
};
